import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { AxiosService } from "../../services/axios/axiosService";
import { Helmet } from "react-helmet-async";
import { APP_TITLE } from "../../constant";
import { ArrowLeft } from "../../components/SvgIcons/SvgIcons";
import { useNavigate } from "react-router-dom";

const LOG_KEY = (id) => `interview_log_${id}`;

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeLog(row, interviewId) {
  // Accept both backend shape and our frontend localStorage shape.
  const eventName = row?.eventName ?? row?.event ?? "-";
  const localTime = row?.localTime ?? "-";
  const questionNum = row?.questionNum ?? row?.question ?? null;
  const questionText = row?.questionText ?? row?.QusetionText ?? row?.QuestionText ?? null;
  const count = row?.count ?? null;
  const timestamp = row?.timestamp ?? "-";
  const totalQuestion = row?.totalQuestion ?? row?.totalQuestions ?? row?.totalQ ?? null;
  //const { state } = useLocation();
  //const [candidateData, setCandidateData] = useState(state?.candidate || null);
  //const canGoBack = !!candidateData;
  return {
    eventName,
    localTime,
    questionNum,
    questionText,
    count,
    timestamp,
    totalQuestion,
    interviewId: row?.interviewId ?? interviewId ?? null,
    _raw: row,
  };
}

const ViewLogs = () => {
  const theme = useTheme();
  const {id} = useParams();
  const {state} = useLocation();
  const candidateInfo = state?.candidateData || {};
  const name = candidateInfo?.name;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  let canBack = true;
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      // 1) Try backend logs endpoint first (expected after /insertLogs)
      try {
        const res = await AxiosService.get(`/api/EventLogs/${id}`);
        //const res = await AxiosService.get(`https://ignascout.igna.ai/api/EventLogs/${id}`);
        const rows = Array.isArray(res) ? res : res?.logs;
        if (Array.isArray(rows)) {
          if (!cancelled) {
            setLogs(rows.map((r) => normalizeLog(r, id)));
            setLoading(false);
          }
          return;
        }
      } catch (e) {
        // fall through to localStorage
      }

      // 2) Fallback to localStorage (useful for local debugging)
      const local = safeJsonParse(localStorage.getItem(LOG_KEY(id)));
      if (Array.isArray(local)) {
        if (!cancelled) {
          setLogs(local.map((r) => normalizeLog(r, id)));
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setLogs([]);
        setError("No logs found for this interview.");
        setLoading(false);
      }
    };

    if (id) load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((r) => {
      const haystack = [
        r.eventName,
        r.localTime,
        r.questionNum,
        r.count,
        r.timestamp,
        r.totalQuestion,
        r.interviewId,
        r.questionText,
      ]
        .filter((v) => v != null)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [logs, query]);

  const handleBack = () => {
    navigate(`/view-recording/${id}`,canBack ? { state: { candidateData: candidateInfo } } : {});
    canBack = true;
  };

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  return (
    <>
      <Helmet>
        <title>View Logs | {APP_TITLE}</title>
        <meta name="View Logs" content="View interview activity logs" />
      </Helmet>

      <Box
        sx={{
          p: 4,
         
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
       <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <Box
                  onClick={handleBack}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    borderRadius: "8px",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#f3f4f6",
                    },
                  }}
                >
                  <ArrowLeft size={24} color={theme.palette.text.primary} />
                </Box>
              
        <Box >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            View Logs
          </Typography>
         
        </Box>
        </Box>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
                : "0px 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}

          > <Typography variant="body2" color="text.secondary">
          Candidate Name: <span style={{ fontWeight: 700,fontSize: 16 }}>{name}</span>
        </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              placeholder="Search event, question, time…"
              sx={{ width: { xs: "100%", md: 360 } }}
            />
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {loading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>             
              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Event Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Question</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question Number</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question Text</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Count</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Local Time</TableCell>
                      {/* <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell> */}
                      {/* <TableCell sx={{ fontWeight: 700 }}>Interview ID</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {error || filtered.length === 0 ? (
                      <TableRow hover>
                        <TableCell >
                          
                            <Typography variant="body2" color="text.secondary" fontWeight={500} py={3}>
                              {error || "No data found."}
                            </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((row, idx) => (
                        <TableRow key={`${row.timestamp}-${idx}`} hover>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {row.eventName}
                          </TableCell>
                          <TableCell>{row.totalQuestion ?? "-"}</TableCell>
                          <TableCell>{row.questionNum ?? "-"}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 520,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.questionText ?? "-"}
                          </TableCell>
                          <TableCell>{row.count ?? "-"}</TableCell>                         
                          <TableCell>{row.localTime ?? "-"}</TableCell>
                          {/* <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {row.timestamp}
                          </TableCell> */}
                          {/* <TableCell>{row.interviewId ?? "-"}</TableCell> */}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default ViewLogs;